import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { useAuth } from '../context/AuthContext';
import { getUsers, updateUserRole, updateUserStages, uploadDesignFile, getUploadedFiles, getAISuggestion, getProductionSettings, updateProductionSettings } from '../services/api';
import { toast } from 'sonner';
import { User, Shield, Upload, FileText, Sparkles, Loader2, Send, Cog, Clock } from 'lucide-react';
import { STAGES } from '../components/prodmgmt/stages';

const SettingsPage = () => {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [aiPrompt, setAiPrompt] = useState('');
  const [aiResponse, setAiResponse] = useState('');
  const [aiLoading, setAiLoading] = useState(false);
  const [assignUser, setAssignUser] = useState(null);
  const [assignStages, setAssignStages] = useState([]);
  const [prodSettings, setProdSettings] = useState({ stall_hours_default: 8, stall_overrides: {} });

  useEffect(() => { getProductionSettings().then((r) => setProdSettings({ stall_hours_default: r.data.stall_hours_default, stall_overrides: r.data.stall_overrides || {} })).catch(() => {}); }, []);

  const setOverride = (key, val) => {
    const next = { ...prodSettings.stall_overrides };
    if (val === '' || val == null) delete next[key]; else next[key] = Number(val);
    setProdSettings({ ...prodSettings, stall_overrides: next });
  };
  const handleSaveProdSettings = async () => {
    try {
      const res = await updateProductionSettings({ stall_hours_default: Number(prodSettings.stall_hours_default) || 8, stall_overrides: prodSettings.stall_overrides });
      setProdSettings({ stall_hours_default: res.data.stall_hours_default, stall_overrides: res.data.stall_overrides || {} });
      toast.success('Delay thresholds saved');
    } catch (e) { toast.error('Failed to save thresholds'); }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      if (user?.role === 'admin') {
        const usersRes = await getUsers();
        setUsers(usersRes.data);
      }
      const filesRes = await getUploadedFiles();
      setFiles(filesRes.data);
    } catch (error) {
      console.error('Failed to load data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleRoleChange = async (userId, newRole) => {
    try {
      await updateUserRole(userId, newRole);
      toast.success('Role updated');
      fetchData();
    } catch (error) {
      toast.error('Failed to update role');
    }
  };

  const openAssign = (u) => { setAssignUser(u); setAssignStages(u.assigned_stages || []); };
  const toggleAssignStage = (key) => setAssignStages((prev) => prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]);
  const handleSaveStages = async () => {
    try {
      await updateUserStages(assignUser.id, assignStages);
      toast.success('Assigned stages updated');
      setAssignUser(null);
      fetchData();
    } catch (e) {
      toast.error('Failed to update assigned stages');
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const allowedExtensions = ['.dxf', '.pdf', '.dwg'];
    const ext = file.name.toLowerCase().slice(file.name.lastIndexOf('.'));
    if (!allowedExtensions.includes(ext)) {
      toast.error(`File type not allowed. Allowed: ${allowedExtensions.join(', ')}`);
      return;
    }

    setUploading(true);
    try {
      const response = await uploadDesignFile(file);
      toast.success('File uploaded successfully');
      if (response.data.mock_bom?.length > 0) {
        toast.info(`Generated BOM with ${response.data.mock_bom.length} items`);
      }
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload file');
    } finally {
      setUploading(false);
    }
  };

  const handleAIQuery = async () => {
    if (!aiPrompt.trim()) {
      toast.error('Please enter a question');
      return;
    }
    setAiLoading(true);
    try {
      const response = await getAISuggestion({ prompt: aiPrompt });
      setAiResponse(response.data.response);
    } catch (error) {
      toast.error('Failed to get AI response');
      setAiResponse('Unable to generate response. Please try again.');
    } finally {
      setAiLoading(false);
    }
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="settings-page">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold tracking-tight">Settings</h1>
        <p className="text-muted-foreground">Manage system settings and configurations</p>
      </div>

      <Tabs defaultValue="profile">
        <TabsList>
          <TabsTrigger value="profile">Profile</TabsTrigger>
          {user?.role === 'admin' && <TabsTrigger value="users">Users</TabsTrigger>}
          {user?.role === 'admin' && <TabsTrigger value="production" data-testid="tab-production-settings">Production</TabsTrigger>}
          <TabsTrigger value="design">Design Files</TabsTrigger>
          <TabsTrigger value="ai">AI Assistant</TabsTrigger>
        </TabsList>

        {/* Profile Tab */}
        <TabsContent value="profile" className="mt-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Profile Information</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-4 mb-6">
                <div className="w-16 h-16 rounded-full bg-accent/20 flex items-center justify-center">
                  <span className="text-accent text-2xl font-bold">
                    {user?.full_name?.charAt(0).toUpperCase()}
                  </span>
                </div>
                <div>
                  <h3 className="font-medium text-lg">{user?.full_name}</h3>
                  <p className="text-muted-foreground">{user?.email}</p>
                  <span className={`status-badge mt-1 ${
                    user?.role === 'admin' ? 'bg-purple-100 text-purple-700' : 'bg-blue-100 text-blue-700'
                  }`}>
                    {user?.role}
                  </span>
                </div>
              </div>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label>Full Name</Label>
                  <Input value={user?.full_name || ''} disabled className="mt-1.5" />
                </div>
                <div>
                  <Label>Email</Label>
                  <Input value={user?.email || ''} disabled className="mt-1.5" />
                </div>
                <div>
                  <Label>Role</Label>
                  <Input value={user?.role || ''} disabled className="mt-1.5 capitalize" />
                </div>
                <div>
                  <Label>Member Since</Label>
                  <Input 
                    value={user?.created_at ? new Date(user.created_at).toLocaleDateString() : ''} 
                    disabled 
                    className="mt-1.5" 
                  />
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Users Tab (Admin Only) */}
        {user?.role === 'admin' && (
          <TabsContent value="users" className="mt-4">
            <Card>
              <CardHeader>
                <CardTitle className="text-base">User Management</CardTitle>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <div className="flex items-center justify-center h-32">
                    <div className="animate-spin rounded-full h-8 w-8 border-2 border-accent border-t-transparent"></div>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="data-table">
                      <thead>
                        <tr>
                          <th>Name</th>
                          <th>Email</th>
                          <th>Role</th>
                          <th>Status</th>
                          <th>Assigned Stages</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {users.map((u) => (
                          <tr key={u.id}>
                            <td>{u.full_name}</td>
                            <td>{u.email}</td>
                            <td>
                              <span className={`status-badge ${
                                u.role === 'admin' ? 'bg-purple-100 text-purple-700' : 'bg-blue-100 text-blue-700'
                              }`}>
                                {u.role}
                              </span>
                            </td>
                            <td>
                              <span className={`status-badge ${u.is_active ? 'status-approved' : 'status-cancelled'}`}>
                                {u.is_active ? 'Active' : 'Inactive'}
                              </span>
                            </td>
                            <td>
                              <div className="flex items-center gap-2">
                                <span className="text-xs text-muted-foreground max-w-[180px] truncate" title={(u.assigned_stages || []).map((k) => { const s = STAGES.find((x) => x.key === k); return s ? s.label : k; }).join(', ')}>
                                  {(u.assigned_stages || []).length === 0 ? 'All stages' : `${u.assigned_stages.length} stage(s)`}
                                </span>
                                <Button variant="outline" size="sm" onClick={() => openAssign(u)} data-testid={`assign-stages-${u.id}`}><Cog size={14} className="mr-1" />Assign</Button>
                              </div>
                            </td>
                            <td>
                              <Select
                                value={u.role}
                                onValueChange={(v) => handleRoleChange(u.id, v)}
                                disabled={u.id === user?.id}
                              >
                                <SelectTrigger className="w-32" data-testid={`user-role-${u.id}`}>
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="admin">Admin</SelectItem>
                                  <SelectItem value="sales">Sales</SelectItem>
                                  <SelectItem value="production">Production</SelectItem>
                                  <SelectItem value="procurement">Procurement</SelectItem>
                                  <SelectItem value="finance">Finance</SelectItem>
                                  <SelectItem value="viewer">Viewer</SelectItem>
                                </SelectContent>
                              </Select>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        )}

        {/* Design Files Tab */}
        <TabsContent value="design" className="mt-4">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-base">Design File Integration</CardTitle>
                <div className="relative">
                  <input
                    type="file"
                    accept=".dxf,.pdf,.dwg"
                    onChange={handleFileUpload}
                    className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                    disabled={uploading}
                    data-testid="file-upload-input"
                  />
                  <Button disabled={uploading} data-testid="upload-design-btn">
                    {uploading ? (
                      <>
                        <Loader2 size={16} className="mr-2 animate-spin" />
                        Uploading...
                      </>
                    ) : (
                      <>
                        <Upload size={16} className="mr-2" />
                        Upload Design
                      </>
                    )}
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-muted-foreground mb-4">
                Upload DXF, DWG, or PDF design files. The system will generate a mock BOM (Bill of Materials) from design files.
              </p>
              {files.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <FileText size={48} className="mx-auto mb-4 opacity-50" />
                  <p>No files uploaded yet</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {files.map((file) => (
                    <div key={file.id} className="flex items-center justify-between p-3 border border-border rounded-sm">
                      <div className="flex items-center gap-3">
                        <FileText size={20} className="text-muted-foreground" />
                        <div>
                          <p className="font-medium text-sm">{file.filename}</p>
                          <p className="text-xs text-muted-foreground">
                            {(file.file_size / 1024).toFixed(1)} KB • {new Date(file.created_at).toLocaleDateString()}
                          </p>
                        </div>
                      </div>
                      <span className="text-xs font-mono bg-muted px-2 py-1 rounded-sm">
                        {file.file_type}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* AI Assistant Tab */}
        <TabsContent value="ai" className="mt-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <Sparkles size={18} className="text-accent" />
                AI Business Assistant
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-muted-foreground mb-4">
                Ask questions about fenestration business, quotation strategies, inventory optimization, and more.
              </p>
              <div className="space-y-4">
                <div className="flex gap-2">
                  <Input
                    value={aiPrompt}
                    onChange={(e) => setAiPrompt(e.target.value)}
                    placeholder="Ask a question about your business..."
                    className="flex-1"
                    data-testid="ai-prompt-input"
                    onKeyDown={(e) => e.key === 'Enter' && handleAIQuery()}
                  />
                  <Button onClick={handleAIQuery} disabled={aiLoading} className="btn-accent" data-testid="ai-submit-btn">
                    {aiLoading ? (
                      <Loader2 size={16} className="animate-spin" />
                    ) : (
                      <Send size={16} />
                    )}
                  </Button>
                </div>
                {aiResponse && (
                  <div className="p-4 bg-muted/30 rounded-sm">
                    <p className="text-sm whitespace-pre-wrap">{aiResponse}</p>
                  </div>
                )}
                <div className="flex flex-wrap gap-2">
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => setAiPrompt('What are the best practices for pricing aluminum windows?')}
                  >
                    Pricing strategies
                  </Button>
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => setAiPrompt('How can I optimize my inventory for seasonal demand?')}
                  >
                    Inventory tips
                  </Button>
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => setAiPrompt('What quality checks should I implement for glass installation?')}
                  >
                    Quality control
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Production settings tab */}
        <TabsContent value="production" className="mt-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2"><Clock size={18} className="text-accent" />Delay Thresholds</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label className="text-sm">Default "too long" limit per stage (hours)</Label>
                <Input type="number" min="1" value={prodSettings.stall_hours_default} onChange={(e) => setProdSettings({ ...prodSettings, stall_hours_default: e.target.value })} className="mt-1 w-40" data-testid="stall-default" />
                <p className="text-xs text-muted-foreground mt-1">A job whose current stage has been in progress longer than this is flagged as delayed on the board.</p>
              </div>
              <div>
                <Label className="text-sm">Per-stage overrides (optional)</Label>
                <div className="grid sm:grid-cols-2 gap-2 mt-2">
                  {STAGES.map((s) => (
                    <div key={s.key} className="flex items-center gap-2">
                      <span className="text-sm flex-1">{s.label}</span>
                      <Input type="number" min="1" placeholder={`${prodSettings.stall_hours_default}h`} value={prodSettings.stall_overrides[s.key] ?? ''} onChange={(e) => setOverride(s.key, e.target.value)} className="w-24 h-8" data-testid={`stall-override-${s.key}`} />
                      <span className="text-xs text-muted-foreground">h</span>
                    </div>
                  ))}
                </div>
              </div>
              <Button className="btn-accent" onClick={handleSaveProdSettings} data-testid="save-prod-settings">Save Thresholds</Button>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Assign stages dialog */}
      <Dialog open={!!assignUser} onOpenChange={(v) => { if (!v) setAssignUser(null); }}>
        <DialogContent>
          <DialogHeader><DialogTitle>Assigned Stages — {assignUser?.full_name}</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">Pick the production stages this operator handles. In the Operator Portal they'll only see jobs currently at these stages. Leave all unchecked to let them see every stage.</p>
          <div className="grid grid-cols-2 gap-2 max-h-72 overflow-y-auto">
            {STAGES.map((s) => (
              <label key={s.key} className="flex items-center gap-2 text-sm border rounded px-2 py-1.5 cursor-pointer hover:bg-muted/40" data-testid={`assign-stage-opt-${s.key}`}>
                <input type="checkbox" checked={assignStages.includes(s.key)} onChange={() => toggleAssignStage(s.key)} />
                {s.label}
              </label>
            ))}
          </div>
          <Button className="w-full btn-accent mt-2" onClick={handleSaveStages} data-testid="save-assign-stages-btn">Save Assignment</Button>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SettingsPage;
